import PropTypes from "prop-types";
import * as React from "react";
import { AllIconNames, Input, InputProps } from "react-ui";
import debounce from "lodash.debounce";

import { Nullable, Override } from "../ReactUIUtilituTypes";
import { getSelectionFromEvent } from "../../../../../Helpers/GetSelectionFromEvent";
import { DatePasteStatus } from "../../../../MultiControls/MultiLine/TableDataPasteContext";
import { negativeValueToBrackets } from "../../../../ValueEditors/Input/InputHelpers";

import { CurrencyHelper } from "./CurrencyHelper";
import { CurrencyInputHelper } from "./CurrencyInputHelper";
import { CURRENCY_INPUT_ACTIONS, extractAction } from "./CurrencyInputKeyboardActions";
import { Selection, SelectionDirection, SelectionHelper } from "./SelectionHelper";

export const isBrowser = typeof window !== "undefined";
const userAgent = ((isBrowser && navigator && navigator.userAgent) || "").toLowerCase();
const isIE11 = /trident\//.test(userAgent);

export type CurrencyInputProps = Override<
    InputProps,
    {
        value: Nullable<string>;
        hideTrailingZeros: boolean;
        fractionDigits?: number;
        signed?: boolean;
        integerDigits?: Nullable<number>;
        showNegativeAsBrackets?: boolean;
        onValueChange: (value: Nullable<string>) => void;
        onSubmit?: () => void;
        useAutoIcon?: AllIconNames;
        onPaste?: (event: React.ClipboardEvent<unknown>) => DatePasteStatus;
    }
>;

export interface CurrencyInputState {
    formatted: string;
    selection: Selection;
    focused: boolean;
}

export class CurrencyInput extends React.Component<CurrencyInputProps, CurrencyInputState> {
    public static __KONTUR_REACT_UI__ = "CurrencyInput";

    public static propTypes = {
        align: PropTypes.oneOf(["left", "center", "right"]),
        autoFocus: PropTypes.bool,
        borderless: PropTypes.bool,
        disabled: PropTypes.bool,
        error: PropTypes.bool,
        fractionDigits: PropTypes.number,
        hideTrailingZeros: PropTypes.bool,
        leftIcon: PropTypes.element,
        placeholder: PropTypes.string,
        signed: PropTypes.bool,
        size: PropTypes.oneOf(["small", "medium", "large"]),
        value: PropTypes.string,
        warning: PropTypes.bool,
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        onBlur: PropTypes.func,
        onValueChange: PropTypes.func.isRequired,
        onFocus: PropTypes.func,
        onMouseEnter: PropTypes.func,
        onMouseLeave: PropTypes.func,
        onMouseOver: PropTypes.func,
        onSubmit: PropTypes.func,
    };

    public static defaultProps = {
        align: "right",
        fractionDigits: 2,
        hideTrailingZeros: false,
        value: null,
        inputMode: "decimal",
    };

    public state: CurrencyInputState = {
        ...this.getState(this.props.value, this.props.fractionDigits, this.props.hideTrailingZeros),
        focused: false,
    };

    private input: Nullable<Input>;
    private tempSelectionForOnChange: Selection = SelectionHelper.fromPosition(0);

    public UNSAFE_componentWillReceiveProps(nextProps: CurrencyInputProps) {
        const { value, fractionDigits, hideTrailingZeros } = nextProps;
        if (value !== CurrencyHelper.parse(this.state.formatted) || fractionDigits !== this.props.fractionDigits) {
            const state = this.getState(value, fractionDigits, hideTrailingZeros);
            this.setState({ formatted: state.formatted });
        }
    }

    public componentDidUpdate(_: Readonly<CurrencyInputProps>, prevState: Readonly<CurrencyInputState>) {
        if (this.props.value && !CurrencyHelper.destructString(this.props.value)) {
            const result = CurrencyHelper.extractValid(this.props.value, {
                integerDigits: this.props.integerDigits,
                fractionDigits: this.props.fractionDigits,
                unsigned: !this.props.signed,
            });
            this.props.onValueChange(result);
        }

        if (this.state.focused && this.input) {
            const { start, end } = this.state.selection;
            this.input.setSelectionRange(start, end);
        }
        if (prevState.selection !== this.state.selection) {
            this.scrollInput();
        }
    }

    public render() {
        const {
            signed: _,
            onSubmit: __,
            integerDigits: ___,
            fractionDigits,
            hideTrailingZeros,
            showNegativeAsBrackets,
            ...rest
        } = this.props;
        const placeholder =
            this.props.placeholder == undefined
                ? CurrencyHelper.format("0", { fractionDigits: fractionDigits, hideTrailingZeros: hideTrailingZeros })
                : this.props.placeholder;

        let value = this.state.formatted;
        if (!this.state.focused && showNegativeAsBrackets) {
            value = negativeValueToBrackets(value);
        }

        return (
            <Input
                {...rest}
                value={value}
                onBlur={this.handleBlur}
                onFocus={this.handleFocus}
                onMouseUp={this.handleMouseUp}
                onKeyDown={this.handleKeyDown}
                onValueChange={this.handleValueChange}
                onPaste={this.handlePaste}
                onCopy={this.handleCopy}
                onCut={this.handleCut}
                onMouseEnter={this.props.onMouseEnter}
                onMouseLeave={this.props.onMouseLeave}
                onMouseOver={this.props.onMouseOver}
                ref={this.refInput}
                placeholder={this.state.focused ? "" : placeholder}
            />
        );
    }

    public blink(): void {
        if (this.input) {
            this.input.focus();
        }
    }

    public focus = () => {
        if (this.input) {
            this.input.focus();
        }
    };

    public blur = () => {
        if (this.input) {
            this.input.blur();
        }
    };

    public selectAll = () => {
        if (this.input) {
            this.input.selectAll();
        }
    };

    private getState(value: Nullable<string>, fractionDigits: Nullable<number>, hideTrailingZeros: boolean) {
        return {
            formatted: CurrencyHelper.format(value, {
                fractionDigits: fractionDigits,
                hideTrailingZeros: hideTrailingZeros,
            }),
            selection: SelectionHelper.fromPosition(0),
        };
    }

    private readonly setSelectionFromEvent = (event: React.MouseEvent<HTMLInputElement>) => {
        const selection = getSelectionFromEvent(event.target);
        const normilized = CurrencyInputHelper.normalizeSelection(this.state.formatted, selection);
        this.setState({ selection: normilized });
    };

    // for IE11
    private readonly debouncedSetSelectionFromEvent = debounce(this.setSelectionFromEvent, 300);

    private readonly handleMouseUp = (event: React.MouseEvent<HTMLInputElement>) => {
        if (isIE11) {
            event.persist();
            this.debouncedSetSelectionFromEvent(event);
        } else {
            this.setSelectionFromEvent(event);
        }
    };

    private readonly handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const selection = this.getSelection(event.target);
        this.tempSelectionForOnChange = selection;

        if (this.props.onKeyDown) {
            this.props.onKeyDown(event);
            if (event.defaultPrevented) {
                return;
            }
        }

        const action = extractAction(event);

        if (action === CURRENCY_INPUT_ACTIONS.Ignore) {
            return;
        }

        if (action !== CURRENCY_INPUT_ACTIONS.Unknown) {
            event.preventDefault();
        }

        switch (action) {
            case CURRENCY_INPUT_ACTIONS.Submit: {
                if (this.props.onSubmit) {
                    this.props.onSubmit();
                }
                break;
            }
            case CURRENCY_INPUT_ACTIONS.Backspace: {
                this.inputValue(CurrencyInputHelper.moveCursor(this.state.formatted, selection, -1), selection.end, "");
                break;
            }
            case CURRENCY_INPUT_ACTIONS.Delete: {
                this.inputValue(
                    selection.start,
                    CurrencyInputHelper.moveCursor(this.state.formatted, selection, +1),
                    ""
                );
                break;
            }
            case CURRENCY_INPUT_ACTIONS.MoveCursorLeft: {
                this.moveCursor(selection, -1);
                break;
            }
            case CURRENCY_INPUT_ACTIONS.MoveCursorRight: {
                this.moveCursor(selection, +1);
                break;
            }
            case CURRENCY_INPUT_ACTIONS.Home: {
                this.setState({ selection: SelectionHelper.fromPosition(0) });
                break;
            }
            case CURRENCY_INPUT_ACTIONS.End: {
                const end = this.state.formatted.length;
                this.setState({ selection: SelectionHelper.fromPosition(end) });
                break;
            }
            case CURRENCY_INPUT_ACTIONS.ExtendSelectionLeft: {
                this.extendSelection(selection, -1);
                break;
            }
            case CURRENCY_INPUT_ACTIONS.ExtendSelectionRight: {
                this.extendSelection(selection, +1);
                break;
            }
            case CURRENCY_INPUT_ACTIONS.FullSelection: {
                this.setState({
                    selection: SelectionHelper.forward(0, this.state.formatted.length),
                });
                break;
            }
            case CURRENCY_INPUT_ACTIONS.ExtendSelectionToStart: {
                this.setState({
                    selection: SelectionHelper.backward(0, selection.start),
                });
                break;
            }
            case CURRENCY_INPUT_ACTIONS.ExtendSelectionToEnd: {
                const inputEnd = this.state.formatted.length;
                this.setState({
                    selection: SelectionHelper.forward(selection.start, inputEnd),
                });
                break;
            }
        }
    };

    private readonly scrollInput = () => {
        const node = this.input?.getNode();
        if (!node || node.scrollWidth === node.clientWidth) {
            return;
        }
        const PAD = 1;
        const SHIFT = 3;

        const selection = this.state.selection;
        const selected = selection.start !== selection.end;
        const position = selected && selection.direction === "forward" ? selection.end : selection.start;
        const charsCount = this.state.formatted.length;
        const charWidth = node.scrollWidth / charsCount;
        const frame = Math.ceil(node.clientWidth / charWidth);
        const frameStart = Math.ceil(node.scrollLeft / charWidth);
        const frameEnd = frameStart + frame;

        if (position < frameStart + PAD) {
            node.scrollLeft = (position - SHIFT) * charWidth;
        }
        if (position > frameEnd - PAD) {
            node.scrollLeft = (position - frame + SHIFT) * charWidth;
        }
    };

    private readonly getSelection = (input: EventTarget): Selection => {
        const selection = getSelectionFromEvent(input);
        return {
            start: selection.start,
            end: selection.end,
            direction: this.state.selection.direction,
        };
    };

    private readonly moveCursor = (selection: Selection, step: number) => {
        const position = CurrencyInputHelper.moveCursor(this.state.formatted, selection, step);
        this.setState({ selection: SelectionHelper.fromPosition(position) });
    };

    private readonly extendSelection = (selection: Selection, step: number) => {
        const extended = CurrencyInputHelper.extendSelection(this.state.formatted, selection, step);
        this.setState({ selection: extended });
    };

    private readonly getSafeInsertValue = (input: string) =>
        CurrencyInputHelper.safeInsert("", 0, 0, input, {
            integerDigits: this.props.integerDigits,
            fractionDigits: this.props.fractionDigits,
            unsigned: !this.props.signed,
        })?.value;

    private readonly inputValue = (start: number, end: number, value: string) => {
        const result = CurrencyInputHelper.safeInsert(this.state.formatted, start, end, value, {
            integerDigits: this.props.integerDigits,
            fractionDigits: this.props.fractionDigits,
            unsigned: !this.props.signed,
        });
        if (result) {
            const formatted = result.value;
            const selection = SelectionHelper.fromPosition(result.position);
            this.setState({ formatted: formatted, selection: selection }, () => {
                const parsedValue = CurrencyHelper.parse(formatted);
                if (this.props.value !== parsedValue) {
                    this.props.onValueChange(parsedValue);
                }
            });
            return true;
        }

        if (this.input) {
            this.input.blink();
        }
        return false;
    };

    private readonly getOnChangeDelta = (value: string) => {
        const selection = this.tempSelectionForOnChange;
        const oldValue = this.state.formatted;
        if (selection.start !== selection.end) {
            return value.substring(selection.start, value.length - (oldValue.length - selection.end));
        } else if (value.length > oldValue.length) {
            return value.substr(selection.start, value.length - oldValue.length);
        }
        return null;
    };

    private readonly handleValueChange = (value: string): void => {
        const selection = this.tempSelectionForOnChange;
        const delta = this.getOnChangeDelta(value);
        if (delta != undefined && !this.inputValue(selection.start, selection.end, delta)) {
            this.setState({ selection: selection });
        }
    };

    private readonly handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const { onPaste } = this.props;
        const result = onPaste && onPaste(event);
        const data = event.clipboardData.getData("text");
        const selection = this.getSelection(event.target);
        const pastedValue = result === DatePasteStatus.Failure ? data : (this.getSafeInsertValue(data) ?? "");
        this.inputValue(selection.start, selection.end, pastedValue);
        event.preventDefault();
    };

    private readonly handleCopy = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const selection = this.getSelection(event.target);
        if (selection.start !== selection.end) {
            const substring = this.state.formatted.substring(selection.start, selection.end);
            const data = CurrencyHelper.formatForClipboard(substring);
            event.clipboardData.setData("text", data);
        }
        event.preventDefault();
    };

    private readonly handleCut = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const selection = this.getSelection(event.target);
        if (selection.start !== selection.end) {
            const substring = this.state.formatted.substring(selection.start, selection.end);
            const data = CurrencyHelper.formatForClipboard(substring);
            event.clipboardData.setData("text", data);
            this.inputValue(selection.start, selection.end, "");
        }
        event.preventDefault();
    };

    private readonly handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        const { selectionStart, selectionEnd, selectionDirection } = event.target;
        const valueLenght = event.target.value.length;

        const selection = {
            start: selectionStart !== selectionEnd ? selectionStart || 0 : selectionStart || valueLenght,
            end: selectionEnd !== selectionStart ? selectionEnd || 0 : selectionEnd || valueLenght,
            direction: (selectionDirection as SelectionDirection) || "none",
        };

        this.setState({
            focused: true,
            selection: selection,
        });

        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    };

    private readonly handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const value = CurrencyHelper.parse(this.state.formatted);

        this.setState({
            ...this.getState(value, this.props.fractionDigits, this.props.hideTrailingZeros),
            focused: false,
        });

        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    };

    private readonly refInput = (element: Nullable<Input>) => {
        this.input = element;
    };
}
